/**
 * Einkaufsliste mit Abgleich: funktioniert offline und hält mehrere Geräte
 * gleichzeitig aktuell.
 *
 * - Jede Änderung wird als Operation in einer Warteschlange gespeichert
 *   (localStorage) und sofort lokal angewendet. Ist der Server erreichbar,
 *   gehen die Operationen in einem Rutsch hin; sonst warten sie, bis wieder
 *   Verbindung besteht.
 * - Die letzte Antwort des Servers liegt ebenfalls im Gerät, damit die Liste
 *   im Laden ohne Netz geöffnet werden kann.
 * - Über einen Ereignisstrom (Server-Sent Events) erfährt das Gerät von
 *   Änderungen anderer Geräte und lädt die Liste nach.
 */
import { guessCategory, shoppingKey } from '@cookbook/domain';
import { useEffect, useSyncExternalStore } from 'react';
import { ApiError, api } from '../api/client.ts';
import type { ShoppingItem, ShoppingList } from '../api/types.ts';

/** Eine Änderung an der Liste, wie sie der Server versteht. */
export type ShoppingOp =
  | {
      type: 'add';
      id: string;
      name: string;
      amount: number | null;
      unit: string;
      note: string | null;
      category?: string;
      at: string;
    }
  | { type: 'check'; id: string; checked: boolean; at: string }
  | {
      type: 'update';
      id: string;
      name?: string;
      amount?: number | null;
      unit?: string;
      note?: string | null;
      category?: string;
      at: string;
    }
  | { type: 'delete'; id: string; at: string }
  /** Von Hand verschoben; order ist die neue Reihenfolge der Zielgruppe. */
  | { type: 'move'; id: string; category: string; order: string[]; at: string }
  | { type: 'clearChecked'; ids: string[]; toPantry: boolean; at: string };

/** Antwort des Servers auf Laden und Operationen. */
interface ListResponse {
  list: ShoppingList;
  items: ShoppingItem[];
}

/** Zustand einer Liste für die Oberfläche. */
export interface ShoppingListState {
  readonly list: ShoppingList | null;
  readonly items: readonly ShoppingItem[];
  /** Noch nicht beim Server angekommene Änderungen. */
  readonly pending: number;
  /** Letzter Versuch, den Server zu erreichen, war erfolgreich. */
  readonly online: boolean;
  readonly loading: boolean;
  readonly error: string | null;
}

/** Kennung dieses Fensters; eigene Änderungen werden im Ereignisstrom erkannt. */
const ORIGIN = `tab-${crypto.randomUUID().slice(0, 12)}`;

/** Abstand für erneute Versuche, solange Änderungen warten. */
const RETRY_MS = 15_000;

/** Liest einen Wert aus dem Gerätespeicher. */
function load<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

/** Schreibt einen Wert in den Gerätespeicher; ein voller Speicher ist kein Fehler. */
function save(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Speicher voll oder gesperrt: Die Liste funktioniert weiter, nur nicht offline.
  }
}

/** Wendet eine Operation auf eine Kopie der Einträge an (vorläufige Anzeige). */
export function applyLocally(items: readonly ShoppingItem[], op: ShoppingOp): ShoppingItem[] {
  switch (op.type) {
    case 'add':
      if (items.some((item) => item.id === op.id)) {
        return [...items];
      }
      return [
        ...items,
        {
          id: op.id,
          key: shoppingKey(op.name),
          name: op.name,
          amount: op.amount,
          unit: op.unit,
          note: op.note,
          category: op.category ?? guessCategory(op.name),
          checked: false,
          checkedBy: null,
          changedAt: op.at,
          position: null,
          sources: [],
          product: null,
        },
      ];
    case 'check':
      return items.map((item) =>
        item.id === op.id ? { ...item, checked: op.checked, changedAt: op.at } : item,
      );
    case 'update':
      return items.map((item) =>
        item.id === op.id
          ? {
              ...item,
              ...(op.name === undefined ? {} : { name: op.name, key: shoppingKey(op.name) }),
              ...(op.amount === undefined ? {} : { amount: op.amount }),
              ...(op.unit === undefined ? {} : { unit: op.unit }),
              ...(op.note === undefined ? {} : { note: op.note }),
              ...(op.category === undefined ? {} : { category: op.category }),
              changedAt: op.at,
            }
          : item,
      );
    case 'delete':
      return items.filter((item) => item.id !== op.id);
    case 'move': {
      const positions = new Map(op.order.map((id, index) => [id, index]));
      return items.map((item) => {
        const position = positions.get(item.id);
        if (position === undefined) {
          return item;
        }
        return item.id === op.id
          ? { ...item, category: op.category, position, changedAt: op.at }
          : { ...item, position };
      });
    }
    case 'clearChecked': {
      const ids = new Set(op.ids);
      return items.filter((item) => !(item.checked && ids.has(item.id)));
    }
    default:
      return [...items];
  }
}

/** Abgleich einer einzelnen Liste. */
class ListSync {
  private server: ListResponse | null;
  private queue: ShoppingOp[];
  private snapshot: ShoppingListState;
  private readonly listeners = new Set<() => void>();
  private inFlight = false;
  private online = true;
  private loading = true;
  private error: string | null = null;
  private retry: number | null = null;

  constructor(private readonly listId: string) {
    this.server = load<ListResponse | null>(this.cacheKey, null);
    this.queue = load<ShoppingOp[]>(this.queueKey, []);
    this.loading = this.server === null;
    this.snapshot = this.compute();
  }

  private get cacheKey(): string {
    return `kochbuch.einkauf.liste.${this.listId}`;
  }

  private get queueKey(): string {
    return `kochbuch.einkauf.warteschlange.${this.listId}`;
  }

  /** Aktueller Zustand für useSyncExternalStore. */
  readonly getSnapshot = (): ShoppingListState => this.snapshot;

  /** Meldet einen Empfänger an. */
  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /** Berechnet den sichtbaren Zustand: Serverstand plus wartende Änderungen. */
  private compute(): ShoppingListState {
    const items = this.queue.reduce<ShoppingItem[]>(
      (current, op) => applyLocally(current, op),
      [...(this.server?.items ?? [])],
    );
    return {
      list: this.server?.list ?? null,
      items,
      pending: this.queue.length,
      online: this.online,
      loading: this.loading,
      error: this.error,
    };
  }

  /** Neu berechnen und Empfänger benachrichtigen. */
  private changed(): void {
    this.snapshot = this.compute();
    for (const listener of this.listeners) {
      listener();
    }
  }

  /** Übernimmt eine Antwort des Servers. */
  private accept(response: ListResponse): void {
    this.server = { list: response.list, items: response.items };
    save(this.cacheKey, this.server);
    this.online = true;
    this.loading = false;
    this.error = null;
  }

  /** Nimmt eine Änderung auf, zeigt sie sofort und schickt sie, sobald es geht. */
  enqueue(op: ShoppingOp): void {
    this.queue.push(op);
    save(this.queueKey, this.queue);
    this.changed();
    void this.flush();
  }

  /** Schickt wartende Änderungen an den Server. */
  async flush(): Promise<void> {
    if (this.inFlight || this.queue.length === 0) {
      return;
    }
    this.inFlight = true;
    const batch = this.queue.slice();
    try {
      const response = await api.post<ListResponse>(
        `/api/shopping/lists/${this.listId}/ops`,
        { ops: batch },
        { 'x-device-id': ORIGIN },
      );
      // Nur die verschickten Operationen entfernen; inzwischen neue bleiben.
      this.queue = this.queue.slice(batch.length);
      save(this.queueKey, this.queue);
      this.accept(response);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 0) {
        this.online = false;
        this.scheduleRetry();
      } else {
        // Der Server lehnt ab (zum Beispiel Liste gelöscht): Änderungen verwerfen.
        this.queue = this.queue.slice(batch.length);
        save(this.queueKey, this.queue);
        this.error = caught instanceof Error ? caught.message : 'Die Änderung wurde abgelehnt.';
      }
    } finally {
      this.inFlight = false;
      this.changed();
    }
    if (this.queue.length > 0 && this.online) {
      void this.flush();
    }
  }

  /** Lädt die Liste vom Server; wartende Änderungen gehen dabei mit. */
  async refresh(): Promise<void> {
    if (this.queue.length > 0) {
      await this.flush();
      return;
    }
    try {
      const response = await api.get<ListResponse>(`/api/shopping/lists/${this.listId}`);
      // Während des Ladens neu entstandene Änderungen haben Vorrang.
      if (this.queue.length === 0) {
        this.accept(response);
      }
    } catch (caught) {
      this.loading = false;
      if (caught instanceof ApiError && caught.status === 0) {
        this.online = false;
        this.scheduleRetry();
      } else {
        this.error = caught instanceof Error ? caught.message : 'Die Liste konnte nicht geladen werden.';
      }
    }
    this.changed();
  }

  /** Plant einen erneuten Versuch. */
  private scheduleRetry(): void {
    if (this.retry !== null) {
      return;
    }
    this.retry = window.setTimeout(() => {
      this.retry = null;
      void this.refresh();
    }, RETRY_MS);
  }

  /** Reagiert auf eine Meldung aus dem Ereignisstrom. */
  notify(revision: number, origin: string | null): void {
    if (origin === ORIGIN) {
      return;
    }
    if (this.server === null || revision > this.server.list.revision) {
      void this.refresh();
    }
  }
}

/** Abgleich je Liste, damit ein Wechsel zwischen Listen nichts neu lädt. */
const syncs = new Map<string, ListSync>();

/** Abgleich einer Liste. */
export function syncFor(listId: string): ListSync {
  let sync = syncs.get(listId);
  if (sync === undefined) {
    sync = new ListSync(listId);
    syncs.set(listId, sync);
  }
  return sync;
}

/** Gemeinsamer Ereignisstrom für alle Listen. */
let events: EventSource | null = null;
let eventUsers = 0;

/** Öffnet den Ereignisstrom, solange eine Liste angezeigt wird. */
function connectEvents(): () => void {
  eventUsers += 1;
  if (events === null && typeof EventSource !== 'undefined') {
    events = new EventSource('/api/shopping/events');
    events.addEventListener('change', (event) => {
      try {
        const change = JSON.parse((event as MessageEvent<string>).data) as {
          listId: string;
          revision: number;
          origin: string | null;
        };
        syncs.get(change.listId)?.notify(change.revision, change.origin);
      } catch {
        // Unlesbare Meldungen ignorieren.
      }
    });
    // Nach einem Abbruch verbindet sich EventSource selbst neu; dann nachladen.
    events.addEventListener('open', () => {
      for (const sync of syncs.values()) {
        void sync.refresh();
      }
    });
  }
  return () => {
    eventUsers -= 1;
    if (eventUsers === 0 && events !== null) {
      events.close();
      events = null;
    }
  };
}

/** Zeitpunkt für eine neue Operation. */
export function opTime(): string {
  return new Date().toISOString();
}

/** Liste mit Abgleich für die Oberfläche. */
export function useShoppingList(listId: string | null): ShoppingListState & { sync: ListSync | null } {
  const sync = listId === null ? null : syncFor(listId);
  const state = useSyncExternalStore(sync?.subscribe ?? noopSubscribe, sync?.getSnapshot ?? emptySnapshot);

  useEffect(() => {
    if (sync === null) {
      return undefined;
    }
    void sync.refresh();
    const disconnect = connectEvents();
    const online = () => void sync.refresh();
    window.addEventListener('online', online);
    return () => {
      window.removeEventListener('online', online);
      disconnect();
    };
  }, [sync]);

  return { ...state, sync };
}

/** Leerer Zustand ohne Liste. */
const EMPTY: ShoppingListState = {
  list: null,
  items: [],
  pending: 0,
  online: true,
  loading: false,
  error: null,
};

/** Ohne Liste gibt es nichts zu abonnieren. */
function noopSubscribe(): () => void {
  return () => undefined;
}

/** Zustand ohne Liste. */
function emptySnapshot(): ShoppingListState {
  return EMPTY;
}
